import React, { useEffect, useState } from "react";
import { Alert, Snackbar } from "@mui/material";
import PermissionMatrix from "./components/PermissionMatrix";
import { useFetchOrganisationRolesMutation } from "../../features/organisation/organisationApiSlice";
import { useSelector, useDispatch } from "react-redux";
import { selectUser } from "../../features/auth/authSlice";
import { useNavigate } from "react-router-dom";
import { isSystemAdmin } from "../../utilities/roleChecks";
import { useGetOrganisationModulesMutation, useGetOrganisationRolesMutation, useGetOrganisationRolesWithPermissionsMutation, useSavePermissionsMutation } from "../../features/rbac/rbacApiSlice";
import ReusableModal from "../../components/Modals/ReusableModal";
import { ACTIVITY_STATUS, ACTIVITY_TYPE } from "../../constants/config.constants";
import { useCreateLogsMutation } from "../../features/logs/logsApiSlice";
import { useGetUserMutation } from "../../features/user/userApiSlice";
import { updateUser } from "../../features/auth/authSlice";

const RBACConfigurationPanel = () => {
    const [roles, setRoles] = useState([]);
    const [modules, setModules] = useState([]);
    const [rolePermissions, setRolePermissions] = useState([]);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = React.useState(false);
    const [originalRolePermissions, setOriginalRolePermissions] = useState([]);
    const [pendingChanges, setPendingChanges] = useState({});
    const [open, setOpen] = React.useState(false);
    const [message, setMessage] = React.useState("Something went wrong");
    const [severity, setSeverity] = React.useState("error");
    const [changesSummary, setChangesSummary] = useState([]); 

    const currentUser = useSelector(selectUser);
    const navigate = useNavigate();

    // Check if user is System Admin, redirect if not
    useEffect(() => {
        if (currentUser && !isSystemAdmin(currentUser)) {
            navigate("/home", { replace: true });
        }
    }, [currentUser, navigate]);

    const [getOrganizationRole] = useFetchOrganisationRolesMutation();
    const [getOrganisationModules] = useGetOrganisationModulesMutation();
    const [getOrganisationRolesPermissions] = useGetOrganisationRolesWithPermissionsMutation();
    const [savePermissions] = useSavePermissionsMutation();
    const [createLog] = useCreateLogsMutation();
    const [getUser, { isLoading: userLoading }] = useGetUserMutation();
    const dispatch = useDispatch();

    const showAlert = (msg, severity) => {
        setOpen(true);
        setMessage(msg);
        setSeverity(severity);
    };

    const getOrganizationRoles = async () => {
        try {
            const result = await getOrganizationRole({ orgId: currentUser.organisation.id })?.unwrap();
            setRoles(result?.data);
        } catch (error) {
            console.log(error)
        }
    }

    const getModules = async () => {
        try {
            const result = await getOrganisationModules()?.unwrap();
            setModules(result?.data?.modules);
        } catch (error) {
            console.log(error)
        }
    }

    const getRolePermissions = async () => {
        try {
            const result = await getOrganisationRolesPermissions()?.unwrap();
            setRolePermissions(result?.data);
            setOriginalRolePermissions(JSON.parse(JSON.stringify(result?.data)));
            setChangesSummary([]); // Reset changes when loading fresh data

        } catch (err) {
            console.log(err)
        }
    }
    const collectAllPermissionIds = (permission) => {
        const ids = [permission.id];
        if (Array.isArray(permission?.sub_permissions)) {
            permission.sub_permissions.forEach(subPerm => {
                ids.push(...collectAllPermissionIds(subPerm));
            });
        }
        return ids;
    };

    // Helper function to check if "view" is the first word of a permission name
    const isViewPermission = (permissionName) => {
        if (!permissionName) return false;
        const name = permissionName.toLowerCase().trim();
        // Split by underscore or dot and check if first word is "view"
        const firstWord = name.split(/[._]/)[0];
        return firstWord === "view";
    };

    const collectViewPermissionIds = (permission) => {
        const ids = [];
        if (isViewPermission(permission.name)) {
            ids.push(permission.id);
        }
        if (Array.isArray(permission?.sub_permissions)) {
            permission.sub_permissions.forEach(subPerm => {
                ids.push(...collectViewPermissionIds(subPerm));
            });
        }
        return ids;
    };

    const handlePermissionChange = (roleId, permission, value) => {
        setRolePermissions(prev =>
          prev.map(role => {
            if (role.id !== roleId) return role;
      
            let updated = new Set(role.permissions);
      
            const isWildcard =
              permission?.name?.endsWith(".*") &&
              Array.isArray(permission?.sub_permissions) &&
              permission.sub_permissions.length > 0;
      
            if (isWildcard) {
              const allSubPermIds = collectAllPermissionIds(permission).filter(id => id !== permission.id);
              const viewSubPermIds = collectViewPermissionIds(permission).filter(id => id !== permission.id);
      
              if (value === "No Access") {
                updated.delete(permission.id);
                allSubPermIds.forEach(id => updated.delete(id));
              }
      
              if (value === "Read Only") {
                updated.add(permission.id);
                viewSubPermIds.forEach(id => updated.add(id));
                allSubPermIds.forEach(id => {
                  if (!viewSubPermIds.includes(id)) {
                    updated.delete(id);
                  }
                });
              }
      
              if (value === "Full Access") {
                updated.add(permission.id);
                allSubPermIds.forEach(id => updated.add(id));
              }
      
              trackChange(roleId, permission.name, "Mixed", value);
      
              return {
                ...role,
                permissions: Array.from(updated)
              };
            }
      
            const hasPermission = updated.has(permission.id);
            let oldValue = "No Access";
      
            if (hasPermission) {
              oldValue = isViewPermission(permission.name)
                ? "Read Only"
                : "Full Access";
            }
      
            if (value === "No Access") {
              updated.delete(permission.id);
              // Optionally remove all nested permissions when parent is removed
              // Uncomment if you want cascading delete behavior:
              // const nestedIds = collectAllPermissionIds(permission).filter(id => id !== permission.id);
              // nestedIds.forEach(id => updated.delete(id));
            } else {
              updated.add(permission.id);
            }
      
            if (oldValue !== value) {
              trackChange(roleId, permission.name, oldValue, value);
            }
      
            return {
              ...role,
              permissions: Array.from(updated)
            };
          })
        );
      };
      

    const findPermissionInTree = (permission, targetName) => {
        if (permission.name === targetName) {
            return permission;
        }
        if (Array.isArray(permission?.sub_permissions)) {
            for (const subPerm of permission.sub_permissions) {
                const found = findPermissionInTree(subPerm, targetName);
                if (found) return found;
            }
        }
        return null;
    };

    const findPermissionByName = (permissionName) => {
        for (const module of modules) {
            for (const feature of module.features || []) {
                for (const perm of feature.permissions || []) {
                    const found = findPermissionInTree(perm, permissionName);
                    if (found) return found;
                }
            }
        }
        return null;
    };

    const applyToAllNested = (permission, roleId, value) => {
        handlePermissionChange(roleId, permission, value);
        if (Array.isArray(permission?.sub_permissions)) {
            permission.sub_permissions.forEach(subPerm => {
                applyToAllNested(subPerm, roleId, value);
            });
        }
    };

    const handleModulePermissionChange = (roleId, module, value) => {
        const childPerms = module?.features?.[0]?.permissions || [];
        const moduleName = module.label;

        setRolePermissions(prev => {
            return prev.map(role => {
                if (role.id !== roleId) return role;

                let updatedPermissionIds = [...role.permissions];
                const oldPermissionIds = [...role.permissions];

                const wildcardPermission = findPermissionByName(`${module.key}.*`);
                const wildcardPermissionId = wildcardPermission?.id;

                const hasPermissionId = (permissionId) => oldPermissionIds.includes(permissionId);

                const getAllPermissionIds = (permissions) => {
                    const ids = [];
                    permissions.forEach(perm => {
                        ids.push(...collectAllPermissionIds(perm));
                    });
                    return ids;
                };

                const getAllViewPermissionIds = (permissions) => {
                    const ids = [];
                    permissions.forEach(perm => {
                        ids.push(...collectViewPermissionIds(perm));
                    });
                    return ids;
                };

                const allChildPermissionIds = getAllPermissionIds(childPerms);
                const allViewPermissionIds = getAllViewPermissionIds(childPerms);
                const allActionPermissionIds = allChildPermissionIds.filter(
                    id => !allViewPermissionIds.includes(id)
                );

                const getOldModuleAccess = () => {
                    if (wildcardPermissionId && hasPermissionId(wildcardPermissionId)) {
                        return "Full Access";
                    }

                    const hasAllPerms = allChildPermissionIds.every(id => hasPermissionId(id));
                    const hasAllViewPerms = allViewPermissionIds.length > 0 &&
                        allViewPermissionIds.every(id => hasPermissionId(id));
                    const hasAnyActionPerms = allActionPermissionIds.some(id => hasPermissionId(id));

                    if (hasAllPerms) return "Full Access";
                    if (hasAllViewPerms && !hasAnyActionPerms) return "Read Only";
                    if (allChildPermissionIds.every(id => !hasPermissionId(id))) return "No Access";

                    return "Partial";
                };

                const oldValue = getOldModuleAccess();

                if (value === "No Access") {
                    updatedPermissionIds = updatedPermissionIds.filter(
                        id => !allChildPermissionIds.includes(id)
                    );

                    if (wildcardPermissionId) {
                        updatedPermissionIds = updatedPermissionIds.filter(
                            id => id !== wildcardPermissionId
                        );
                    }
                } else if (value === "Read Only") {
                    updatedPermissionIds = updatedPermissionIds.filter(
                        id => !allChildPermissionIds.includes(id)
                    );

                    if (wildcardPermissionId) {
                        updatedPermissionIds = updatedPermissionIds.filter(
                            id => id !== wildcardPermissionId
                        );
                    }

                    updatedPermissionIds = [...new Set([...updatedPermissionIds, ...allViewPermissionIds])];
                } else if (value === "Full Access") {
                    if (wildcardPermissionId) {
                        updatedPermissionIds = updatedPermissionIds.filter(
                            id => id !== wildcardPermissionId
                        );
                    }

                    updatedPermissionIds = [...new Set([...updatedPermissionIds, ...allChildPermissionIds])];
                }

                if (oldValue !== value) {
                    trackChange(roleId, module.key, oldValue, value, moduleName);
                }

                return {
                    ...role,
                    permissions: updatedPermissionIds
                };
            });
        });
    };

    const trackChange = (roleId, permission, oldValue, newValue, moduleName = null) => {
        const role = roles.find(r => r.id === roleId);
        if (!role) return;

        const change = {
            roleName: role.name,
            permission,
            oldValue,
            newValue,
            moduleName,
            timestamp: new Date().toISOString()
        };

        setChangesSummary(prev => [...prev, change]);
    };
    const generateChangesSummaryText = () => {
        if (changesSummary.length === 0) return "No changes made";

        const roleChanges = {};

        changesSummary.forEach(change => {
            if (!roleChanges[change.roleName]) {
                roleChanges[change.roleName] = [];
            }

            if (change.moduleName) {
                roleChanges[change.roleName].push(
                    `${change.moduleName}: ${change.oldValue} → ${change.newValue}`
                );
            } else {
                roleChanges[change.roleName].push(
                    `${change.permission}: ${change.oldValue} → ${change.newValue}`
                );
            }
        });

        let summary = "Changes: ";
        Object.keys(roleChanges).forEach(roleName => {
            summary += `\n• ${roleName}: ${roleChanges[roleName].join(", ")}`;
        });

        return summary;
    };

    const fetchUser = async () => {
        try {
            const { data } = await getUser().unwrap();

            dispatch(
                updateUser({
                    user: data,
                })
            );
        } catch (error) {
            console.error("Failed to fetch user: ", error);
        }
    };
    const handleSave = async () => {
        try {
            const payload = {
                activity: `RBAC permissions updated by User - ${currentUser?.details?.name}`,
                type: ACTIVITY_TYPE.RBAC_CONFIGURATION_UPDATE,
                metadata: {
                    changesCount: changesSummary.length,
                    changesSummary: generateChangesSummaryText(),
                    affectedRoles: [...new Set(changesSummary.map(c => c.roleName))],
                    timestamp: new Date().toISOString()
                }
            };
            try {
                await savePermissions({ permissions: rolePermissions })?.unwrap();

                await createLog({
                    ...payload,
                    status: ACTIVITY_STATUS.SUCCESS
                }).unwrap();
            } catch (err) {
                await createLog({
                    ...payload,
                    status: ACTIVITY_STATUS.FAILED,
                    metadata: {
                        ...payload.metadata,
                        error: err.message || "Unknown error"
                    }
                }).unwrap();
            }
            getRolePermissions();
            fetchUser();
            setChangesSummary([]);
            showAlert("Permissions updated successfully.", "success");
        } catch (err) {
            console.log(err);

            showAlert(err.data.message, "error")
        }

    };
    const handleCancel = () => {
        setRolePermissions(JSON.parse(JSON.stringify(originalRolePermissions)));
    };
    useEffect(() => {
        getOrganizationRoles();
        getModules();
        getRolePermissions();
    }, []);
    return (
        <div className="py-3">
            <Snackbar
                onClose={() => setOpen(false)}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                open={open}
                autoHideDuration={5000}
                message={message ? message : "Something went wrong"}
                severity="error"
            >
                <Alert
                    onClose={() => setOpen(false)}
                    severity={severity ? severity : "error"}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {message ? message : "Something went wrong"}
                </Alert>
            </Snackbar>
            {isConfirmModalOpen && (
                <ReusableModal
                    open={isConfirmModalOpen}
                    title={'Save Permission Changes'}
                    onClose={() => setIsConfirmModalOpen(false)}
                >
                    <span
                        className="flex-col d-flex"
                        style={{
                            padding: '10px 10px 10px 10px ',
                            minWidth: '35vw',
                        }}
                    >
                        
                        <div className="flex flex-col gap-1 mb-2">
                            {`Are you sure you want to update these permissions? This action will affect user access.`}
                        </div>

                        <div className="gap-2 justify-end d-flex mt-3">
                            <button
                                className="px-3 py-2 text-sm font-semibold text-white rounded-md bg-primaryblue"
                                onClick={() => {
                                    setIsConfirmModalOpen(false);
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                className="px-3 py-2 text-sm font-semibold text-white rounded-md bg-primaryblue"
                                onClick={() => {
                                    setIsConfirmModalOpen(false)
                                    handleSave();
                                }}
                            >
                                Yes
                            </button>
                        </div>
                    </span>
                </ReusableModal>
            )}
            <span className="flex flex-col px-3">
                <h3 className="text-gray-800 font-semibold">
                    Role-Based Access Control (RBAC) Configuration
                </h3>
                <p className="text-gray-500 font-semibold text-sm">
                    Configure permissions for each role across modules and
                    features. Changes will apply to all users with these roles.
                </p>
                <div className="bg-blue-50 w-full border-l-4 border-blue-500 p-3 rounded-md mb-2">
                    <p className="mb-0 text-[16px] font-bold text-blue-700">
                        Note:{' '}
                        <span className="font-bold">
                            Full Access = Create/Edit/Delete, Read Only = View
                            Only, No Access = hidden. Module rows show overall
                            access level.
                        </span>
                    </p>
                </div>
                <PermissionMatrix
                    roles={roles}
                    modules={modules}
                    rolePermissions={rolePermissions}
                    handlePermissionChange={handlePermissionChange}
                    handleModulePermissionChange={handleModulePermissionChange}
                />
            </span>
            <div className="flex justify-end border-[#ddd] border-t fixed w-full bg-white bottom-0 py-2 gap-3 px-3">
                <button className="px-4 py-2 text-gray-700 rounded-md border border-gray-300 hover:bg-gray-100"
                    onClick={() => {
                        handleCancel();
                    }}

                >
                    Cancel
                </button>
                <button
                    className={`px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700  disabled:cursor-not-allowed disabled:opacity-50
                  `}
                    onClick={() => {
                        setIsConfirmModalOpen(true);
                    }}
                >
                    Save Changes
                </button>
            </div>
        </div>
    );
};

export default RBACConfigurationPanel;
